import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

import LoginPage from './components/pages/LoginPage';
import DashboardPage from './components/pages/DashboardPage';
import AdvansityDashboard from './components/pages/AdvansityDashboard';
import UsersPage from './components/pages/UsersPage';
import NewsPage from './components/pages/NewsPage';
import CategoryPage from './components/pages/CategoryPage';
import MainCategoryPage from './components/pages/MainCategoryPage';
import AdsPage from './components/pages/AdsPage';
import NotificationsPage from './components/pages/NotificationsPage';
import GaragePage from './components/pages/GaragePage';
import VehicleDetailPage from './components/pages/VehicleDetailPage';
import ConfigPage from './components/pages/ConfigPage';
import PracticePage from './components/pages/PracticePage';
import TestsPage from './components/pages/TestsPage';
import LearningPage from './components/pages/LearningPage';
import HomeworkPage from './components/pages/HomeworkPage';
import QuizForm from './components/pages/QuizForm';
import QuizDetail from './components/pages/QuizDetail';
import QuestionList from './components/pages/QuestionList';
import QuestionForm from './components/pages/QuestionForm';
import SchoolList from './components/pages/SchoolList';
import SchoolForm from './components/pages/SchoolForm';
import TeacherList from './components/pages/TeacherList';
import TeacherForm from './components/pages/TeacherForm';
import TeacherBulkUpload from './components/pages/TeacherBulkUpload';
import StudentList from './components/pages/StudentList';
import StudentForm from './components/pages/StudentForm';
import StudentBulkUpload from './components/pages/StudentBulkUpload';
import ResultList from './components/pages/ResultList';
import CredHomePage from './components/pages/CredHomePage';
import ProfilePage from './components/pages/ProfilePage';
import SettingsPage from './components/pages/SettingsPage';
import ChangePasswordPage from './components/pages/ChangePasswordPage';

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F9FAFB' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid #E5E7EB', borderTopColor: '#4F46E5', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#6B7280', fontSize: 14 }}>Loading...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Public route (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
    <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
    <Route path="/advansity-dashboard" element={<ProtectedRoute><AdvansityDashboard /></ProtectedRoute>} />
    <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
    <Route path="/news" element={<ProtectedRoute><NewsPage /></ProtectedRoute>} />
    <Route path="/main-categories" element={<ProtectedRoute><MainCategoryPage /></ProtectedRoute>} />
    <Route path="/categories" element={<ProtectedRoute><CategoryPage /></ProtectedRoute>} />
    <Route path="/ads" element={<ProtectedRoute><AdsPage /></ProtectedRoute>} />
    <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
    <Route path="/garage" element={<ProtectedRoute><GaragePage /></ProtectedRoute>} />
    <Route path="/garage/:id" element={<ProtectedRoute><VehicleDetailPage /></ProtectedRoute>} />
    {/* PAL Practice */}
    <Route path="/pal/practice" element={<ProtectedRoute><PracticePage /></ProtectedRoute>} />
    <Route path="/pal/practice/add" element={<ProtectedRoute><QuizForm defaultType="Practice" basePath="/pal/practice" /></ProtectedRoute>} />
    <Route path="/pal/practice/edit/:id" element={<ProtectedRoute><QuizForm defaultType="Practice" basePath="/pal/practice" /></ProtectedRoute>} />
    <Route path="/pal/practice/:id" element={<ProtectedRoute><QuizDetail basePath="/pal/practice" /></ProtectedRoute>} />

    {/* PAL Tests */}
    <Route path="/pal/tests" element={<ProtectedRoute><TestsPage /></ProtectedRoute>} />
    <Route path="/pal/tests/add" element={<ProtectedRoute><QuizForm defaultType="Tests" basePath="/pal/tests" /></ProtectedRoute>} />
    <Route path="/pal/tests/edit/:id" element={<ProtectedRoute><QuizForm defaultType="Tests" basePath="/pal/tests" /></ProtectedRoute>} />
    <Route path="/pal/tests/:id" element={<ProtectedRoute><QuizDetail basePath="/pal/tests" /></ProtectedRoute>} />

    {/* PAL Learning */}
    <Route path="/pal/learning" element={<ProtectedRoute><LearningPage /></ProtectedRoute>} />
    <Route path="/pal/learning/add" element={<ProtectedRoute><QuizForm defaultType="Learning" basePath="/pal/learning" /></ProtectedRoute>} />
    <Route path="/pal/learning/edit/:id" element={<ProtectedRoute><QuizForm defaultType="Learning" basePath="/pal/learning" /></ProtectedRoute>} />
    <Route path="/pal/learning/:id" element={<ProtectedRoute><QuizDetail basePath="/pal/learning" /></ProtectedRoute>} />

    {/* PAL Homework */}
    <Route path="/pal/homework" element={<ProtectedRoute><HomeworkPage /></ProtectedRoute>} />
    <Route path="/pal/homework/add" element={<ProtectedRoute><QuizForm defaultType="Homework" basePath="/pal/homework" /></ProtectedRoute>} />
    <Route path="/pal/homework/edit/:id" element={<ProtectedRoute><QuizForm defaultType="Homework" basePath="/pal/homework" /></ProtectedRoute>} />
    <Route path="/pal/homework/:id" element={<ProtectedRoute><QuizDetail basePath="/pal/homework" /></ProtectedRoute>} />

    {/* Legacy Quiz redirects */}
    <Route path="/quiz" element={<Navigate to="/pal/practice" replace />} />
    <Route path="/quiz/add" element={<Navigate to="/pal/practice/add" replace />} />
    <Route path="/quiz/edit/:id" element={<Navigate to="/pal/practice" replace />} />
    <Route path="/quiz/:id" element={<Navigate to="/pal/practice" replace />} />
    <Route path="/config/board" element={<ProtectedRoute><ConfigPage /></ProtectedRoute>} />
    <Route path="/config/exam" element={<ProtectedRoute><ConfigPage /></ProtectedRoute>} />
    <Route path="/config/class" element={<ProtectedRoute><ConfigPage /></ProtectedRoute>} />
    <Route path="/config/subject" element={<ProtectedRoute><ConfigPage /></ProtectedRoute>} />
    <Route path="/config/topic" element={<ProtectedRoute><ConfigPage /></ProtectedRoute>} />
    <Route path="/config/sub-topic" element={<ProtectedRoute><ConfigPage /></ProtectedRoute>} />
    <Route path="/config/questions" element={<ProtectedRoute><QuestionList /></ProtectedRoute>} />
    <Route path="/config/questions/add" element={<ProtectedRoute><QuestionForm /></ProtectedRoute>} />
    <Route path="/schools" element={<ProtectedRoute><SchoolList /></ProtectedRoute>} />
    <Route path="/school/add" element={<ProtectedRoute><SchoolForm /></ProtectedRoute>} />
    <Route path="/school/edit/:id" element={<ProtectedRoute><SchoolForm /></ProtectedRoute>} />
    <Route path="/teachers" element={<ProtectedRoute><TeacherList /></ProtectedRoute>} />
    <Route path="/teacher/add" element={<ProtectedRoute><TeacherForm /></ProtectedRoute>} />
    <Route path="/teacher/edit/:id" element={<ProtectedRoute><TeacherForm /></ProtectedRoute>} />
    <Route path="/teacher/bulk-upload" element={<ProtectedRoute><TeacherBulkUpload /></ProtectedRoute>} />
    <Route path="/students" element={<ProtectedRoute><StudentList /></ProtectedRoute>} />
    <Route path="/student/add" element={<ProtectedRoute><StudentForm /></ProtectedRoute>} />
    <Route path="/student/edit/:id" element={<ProtectedRoute><StudentForm /></ProtectedRoute>} />
    <Route path="/student/bulk-upload" element={<ProtectedRoute><StudentBulkUpload /></ProtectedRoute>} />
    <Route path="/results/tests" element={<ProtectedRoute><ResultList /></ProtectedRoute>} />
    <Route path="/results/practice" element={<ProtectedRoute><ResultList /></ProtectedRoute>} />
    <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
    <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
    <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
    <Route path="/" element={<Navigate to="/dashboard" replace />} />
    <Route path="/home" element={<CredHomePage />} />
    <Route path="*" element={<Navigate to="/dashboard" replace />} />
  </Routes>
);

const App = () => (
  <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  </BrowserRouter>
);

export default App;
